import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMessage } from '../contexts/MessageContext';
import { useAuth  } from '../contexts/AuthContext';
import { Table } from './common/Table';
import { Loading } from './common/Loading';
import RelatedItems  from './common/ReelatedItems';
import DateTimeDisplay from '../components/DateTimeDisplay';
import { calcInvoiceFinancials } from '../utils/invoiceTotals';

const apiUrl = window._env_?.REACT_APP_API_URL || "https://app.mylocaleventrentals.com/api";

const normalizeInvoiceLine = (line, orderData = null) => {
  const orderItem = orderData?.items?.find((item) => item.inventoryId && item.inventoryId === line.item_id);
  const description =
    line.description ||
    line.inventory?.name ||
    line.Inventory?.name ||
    orderItem?.inventory?.name ||
    orderItem?.name ||
    "";

  const qty = parseFloat(line.quantity) || 0;
  const price = parseFloat(line.unit_price) || 0;
  return {
    ...line,
    description,
    line_total: line.line_total ?? (qty * price).toFixed(2),
  };
};

const InvoiceView = () => {
  const columns = [
    { key: 'description', label: 'Description', sortable: true },
    { key: 'quantity', label: 'quantity', sortable: false },
    { key: 'unit_price', label: 'Unit Price', sortable: false },
    { key: 'line_total', label: 'Total', sortable: false }
  ];

  const { uuid } = useParams();
  const [invoice, setInvoice] = useState([]);
  const [order, setOrder] = useState(null);
  const [payments, setPayments] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [customer, setCustomer] = useState([]);
  const [invoiceLines, setInvoiceLines] = useState([
    { description: "", quantity: 1, unit_price: 0, total: 0 }
  ]);

  const [loading, setLoading] = useState(true);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const {showError} = useMessage();

  // Fetch invoices
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      logout();
      navigate("/login");
      return;
    }

    const fetchData = async () => {
      try {
        // 1️⃣ Get the invoice first
        const invoiceRes = await axios.get(`${apiUrl}/invoice/${uuid}`, {
          headers: { "x-auth-token": token },
        });

        const invoiceData = invoiceRes.data.data;
        setInvoice(invoiceData);
        setCustomer(invoiceData.Customer);

        // 2️⃣ Now get the related order — only if invoiceData.order_id exists
        let orderData = null;
        if (invoiceData.order_id) {
          const orderRes = await axios.get(`${apiUrl}/order/${invoiceData.order_id}`, {
            headers: { "x-auth-token": token },
          });
          orderData = orderRes.data;
          setOrder(orderData);
        } else {
          setOrder(null);
        }
        setInvoiceLines((invoiceData.InvoiceLines || []).map((line) => normalizeInvoiceLine(line, orderData)));

        //get payments related to this invoice
          const paymentRes = await axios.get(`${apiUrl}/payment/invoice/${uuid}/payments`, {
            headers: { "x-auth-token": token },
          });
          setPayments(paymentRes.data);

        // get activity log
        try {
          const logRes = await axios.get(`${apiUrl}/invoice/${uuid}/logs`, {
            headers: { "x-auth-token": token },
          });
          if (logRes.data.success) setActivityLogs(logRes.data.data);
        } catch (_) {}

      } catch (err) {
        console.error("Error fetching data:", err);
        showError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, navigate, logout]);


  const [logoUrl, setLogoUrl] = useState("");
  const [companyLegalName, setCompanyLegalName] = useState("");
  useEffect(() => {
    axios.get(`${apiUrl}/settings/branding`).then((res) => {
      if (res.data.logo_url) setLogoUrl(res.data.logo_url);
      if (res.data.company_legal_name) setCompanyLegalName(res.data.company_legal_name);
    }).catch(() => {
      axios.get(`${apiUrl}/settings/logo`).then((r) => {
        if (r.data.logo_url) setLogoUrl(r.data.logo_url);
      });
    });
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="p-4 md:p-6 my-2 md:my-6 bg-white shadow-lg rounded-lg">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-lg overflow-hidden">
          {/* Header */}
          <div className="flex items-center mb-4">
            {logoUrl && (
              <img src={logoUrl} alt="Invoice Logo" className="h-16 mr-4 object-contain border rounded" />
            )}
            <div>
              {companyLegalName ? (
                <p className="text-sm font-semibold text-gray-600">{companyLegalName}</p>
              ) : null}
              <h2 className="text-2xl font-bold text-gray-800">Invoice Details</h2>
            </div>
          </div>
        {/* ✅ Customer Details Section (conditional render) */}
        {customer && (
          <div className="p-6 border border-gray-200 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Customer Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-gray-500">First Name</p>
                <p className="text-base font-medium text-gray-900">
                  {customer.firstname || 'No customer firstname'}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-gray-500">Last Name</p>
                <p className="text-base font-medium text-gray-900">
                  {customer.lastname || 'No customer lastname'}
                </p>
              </div>
              <div className="space-y-1 sm:col-span-2">
                <p className="text-sm text-gray-500">Email</p>
                <p className="text-base font-medium text-gray-900">
                  {customer.email || 'No customer email'}
                </p>
              </div>
            </div>
          </div>
        )}
 
         {/* Order details*/}
        {order && (
          <div className="p-6 border border-gray-200 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Order Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-gray-500">Order Number</p>
                <p className="text-base font-medium text-gray-900">
                  {order.uuid ? (
                    <Link
                      to={`/orders/${order.uuid}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {order.formattedOrderNumber ||
                        (order.orderNumber != null ? `Order #${order.orderNumber}` : 'View order')}
                    </Link>
                  ) : (
                    order.formattedOrderNumber || 'No order associated'
                  )}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-gray-500">Order Status</p>
                <p className="text-base font-medium text-gray-900">
                  {order.status || 'No order associated'}
                </p>
              </div>
              <div className="space-y-1 sm:col-span-2">
                <p className="text-sm text-gray-500">Payment Status</p>
                <p className="text-base font-medium text-gray-900">
                  {order.paymentStatus || 'No order associated'}
                </p>
              </div>
            </div>
          </div>
        )}

          {/* Invoice Details Section */}
          <div className="p-6 border shadow-md">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Invoice Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-gray-500">Invoice Number</p>
                <p className="text-base font-medium text-gray-900">{invoice.invoiceNumber}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-gray-500">Date</p>
                <p className="text-base font-medium text-gray-900">{invoice.date}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-gray-500">Amount</p>
                <p className="text-base font-medium text-gray-900">${parseFloat(invoice.total_amount || 0).toFixed(2)}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-gray-500">Status</p>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  {invoice.status}
                </span>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-gray-500">Notes</p>
                <p className="text-base font-medium text-gray-900">{invoice.notes || 'No notes available'}</p>
              </div>
            </div>
          </div>
          <Table
            columns={columns}
            data={invoiceLines}
            onRowClick={(row) => console.log('Clicked:', row)}
          />

          {/* Discount / Total breakdown */}
          {(() => {
            const { subtotal, discountAmt, total, discountValue, discountType } =
              calcInvoiceFinancials({ ...invoice, InvoiceLines: invoiceLines });
            return (
              <div className="px-6 pb-4">
                <table className="w-full text-sm">
                  <tbody>
                    <tr>
                      <td className="text-right py-1 text-gray-500">Subtotal</td>
                      <td className="text-right py-1 w-32 text-gray-700">${subtotal.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="text-right py-1 text-gray-500">
                        Discount {discountType === 'percent' && discountValue > 0 ? `(${discountValue}%)` : ''}
                      </td>
                      <td className={`text-right py-1 w-32 ${discountAmt > 0 ? 'text-red-600' : 'text-gray-700'}`}>
                        - ${discountAmt.toFixed(2)}
                      </td>
                    </tr>
                    <tr className="border-t-2 border-gray-200 font-bold">
                      <td className="text-right py-2 text-gray-800">Total</td>
                      <td className="text-right py-2 w-32 text-blue-700">${total.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Share link */}
          {invoice.share_token && (
            <div className="px-6 pb-4">
              <p className="text-sm text-gray-500 mb-1">Customer Link</p>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={`${window.location.origin}/invoice/view/${invoice.share_token}`}
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm text-gray-700 bg-gray-50"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/invoice/view/${invoice.share_token}`);
                  }}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                >
                  Copy
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="px-6 py-4 bg-gray-50 flex flex-col sm:flex-row gap-3 sm:justify-end">
            <button
              onClick={() => navigate(`/invoice/${uuid}/edit`)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Edit Invoice
            </button>
            <button
              onClick={() => window.print()}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Print / Save PDF
            </button>
          </div>
          <div>
            <RelatedItems
              title="Payments"
              data={payments?.data}
              showAddButton
              addButtonLabel="Record payment"
              onAdd={() => navigate(`/payment/new?invoiceId=${uuid}`)}
              columns={[
                { key: 'createdAt', label: 'Date', render: (value, row)  => <DateTimeDisplay value={row.createdAt} />},
                { key: 'amount', label: 'Amount' },
                { key: 'status', label: 'Status' }
              ]}
              />
          </div>

          {/* Activity Log */}
          <div className="p-6 border border-gray-200 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Customer Activity</h2>
            {activityLogs.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No activity yet.</p>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-2">Date &amp; Time</th>
                    <th className="text-left px-4 py-2">Event</th>
                    <th className="text-left px-4 py-2">IP Address</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {activityLogs.map(log => (
                    <tr key={log.uuid} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          log.event === 'viewed' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {log.event.charAt(0).toUpperCase() + log.event.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-500 font-mono text-xs">{log.ip_address || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceView;
